/* eslint-disable max-lines */
import * as c from './types';

import {
  BasicProductionAdvancementType,
  CreateBasicProductionAdvancementParams,
  CreateFullProductionAdvancementBulkParams,
  CreateProductionDataBulkParams,
  CreateProductionDataParams,
  DeleteProductionDataParams,
  GetBasicProductionDataParams,
  GetProductionDataParams,
  IProductionService,
  UpdateProductionDataParams
} from './../../services/contracts/production.service';
import { ICustomFieldsService } from '_clean-code/services/contracts/customFields.service';
import {
  GetCustomFieldsParams,
  ECustomFieldEntity,
  CustomFieldEntry
} from '@clean-code/types/custom-fields';

import AppServices from '_clean-code/services';
import { I18n } from '@utils/i18n';
import { ProductionConfig } from './reducers';
import ServiceContext from '_clean-code/services/context/service.context';
import createServiceAction from '_clean-code/services/stateManager/createServiceAction';
import { toast as radixToast } from '@pulsarml/avalon-ui';
import { toast } from 'react-toastify';
import toastr from '@utils/toastr';

export const getProductionData = (params: GetProductionDataParams) =>
  createServiceAction({
    doAction: c.FETCH_PRODUCTION_DATA,
    successAction: c.FETCH_PRODUCTION_DATA_SUCCESS,
    errorAction: c.FETCH_PRODUCTION_DATA_ERROR,
    do: async () => {
      const productionService = ServiceContext.resolve<IProductionService>(
        AppServices.ProductionService
      );
      const data = await productionService.getAllProduction(params);
      return data;
    },
    errorHandler({ response }) {
      if (!response || !response.data) {
        toastr.error(
          I18n.t('system_popup'),
          I18n.t('error_getting_production_data')
        );
        return;
      }
      const { error } = response.data;
      toastr.error(I18n.t('system_popup'), error);
    }
  });

export const getBasicProductionData = (params: GetBasicProductionDataParams) =>
  createServiceAction({
    doAction: c.FETCH_BASIC_PRODUCTION_DATA,
    successAction: c.FETCH_BASIC_PRODUCTION_DATA_SUCCESS,
    errorAction: c.FETCH_BASIC_PRODUCTION_DATA_ERROR,
    do: async (): Promise<BasicProductionAdvancementType[]> => {
      const productionService = ServiceContext.resolve<IProductionService>(
        AppServices.ProductionService
      );
      const data = await productionService.getBasicProductionAdvancement(
        params
      );
      return data;
    },
    errorHandler({ response }) {
      if (!response || !response.data) {
        toastr.error(
          I18n.t('system_popup'),
          I18n.t('error_getting_production_batches')
        );
        return;
      }
      const { error } = response.data;
      toastr.error(I18n.t('system_popup'), error);
    }
  });

export const createProductionData = (params: CreateProductionDataParams) =>
  createServiceAction({
    doAction: c.CREATE_PRODUCTION_DATA,
    successAction: c.CREATE_PRODUCTION_DATA_SUCCESS,
    errorAction: c.CREATE_PRODUCTION_DATA_ERROR,
    do: async () => {
      const productionService = ServiceContext.resolve<IProductionService>(
        AppServices.ProductionService
      );
      const data = await productionService.createProduction(params);
      return data;
    },
    errorHandler({ response }) {
      if (response?.data?.message === 'The Work step is duplicated.') {
        toast.error(I18n.t('work_step_duplicated'));
        return;
      }
      if (response?.data?.message === 'The Work step has overlaps.') {
        toast.error(I18n.t('work_step_has_overlaps'));
        return;
      }
      const { error } = response.data;
      toastr.error(I18n.t('system_popup'), error);
    }
  });

export const createBasicProductionAdvancement = (
  params: CreateBasicProductionAdvancementParams
) =>
  createServiceAction({
    doAction: c.CREATE_BASIC_PRODUCTION_ADVANCEMENT,
    successAction: c.CREATE_BASIC_PRODUCTION_ADVANCEMENT_SUCCESS,
    errorAction: c.CREATE_BASIC_PRODUCTION_ADVANCEMENT_ERROR,
    do: async () => {
      const productionService = ServiceContext.resolve<IProductionService>(
        AppServices.ProductionService
      );
      await productionService.createBasicProductionAdvancement(params);
      radixToast({
        variant: 'success',
        title: I18n.t('production_advancement_added_successfully'),
        subtitle: I18n.t('you_can_check_it_on_the_table')
      });
    },
    errorHandler({ response }) {
      if (response?.data?.message === 'The Work step is duplicated.') {
        radixToast({
          variant: 'error',
          title: I18n.t('there_is_some_error'),
          subtitle: I18n.t('work_step_duplicated')
        });
        return;
      }
      if (
        response?.data?.message === 'The Production advancement has overlaps.'
      ) {
        radixToast({
          variant: 'error',
          title: I18n.t('there_is_some_error'),
          subtitle: I18n.t('production_advancement_has_overlaps')
        });
        return;
      }
      const error = response?.data?.message;
      radixToast({
        variant: 'error',
        title: I18n.t('there_is_some_error'),
        subtitle: error
      });
    }
  });

export const updateBasicProductionAdvancement = (
  params: CreateBasicProductionAdvancementParams
) =>
  createServiceAction({
    doAction: c.UPDATE_BASIC_PRODUCTION_ADVANCEMENT,
    successAction: c.UPDATE_BASIC_PRODUCTION_ADVANCEMENT_SUCCESS,
    errorAction: c.UPDATE_BASIC_PRODUCTION_ADVANCEMENT_ERROR,
    do: async () => {
      const productionService = ServiceContext.resolve<IProductionService>(
        AppServices.ProductionService
      );
      await productionService.updateBasicProductionAdvancement(params);
      radixToast({
        variant: 'success',
        title: I18n.t('production_advancement_updated_successfully'),
        subtitle: I18n.t('you_can_check_it_on_the_table')
      });
    },
    errorHandler({ response }) {
      if (response?.data?.message === 'The Work step is duplicated.') {
        radixToast({
          variant: 'error',
          title: I18n.t('there_is_some_error'),
          subtitle: I18n.t('work_step_duplicated')
        });
        return;
      }
      if (
        response?.data?.message === 'The Production advancement has overlaps.'
      ) {
        radixToast({
          variant: 'error',
          title: I18n.t('there_is_some_error'),
          subtitle: I18n.t('production_advancement_has_overlaps')
        });
        return;
      }
      const error = response?.data?.message;
      radixToast({
        variant: 'error',
        title: I18n.t('there_is_some_error'),
        subtitle: error
      });
    }
  });
export const splitBasicProductionAdvancement = (
  id: string,
  params: CreateBasicProductionAdvancementParams[]
) =>
  createServiceAction({
    doAction: c.SPLIT_BASIC_PRODUCTION_ADVANCEMENT,
    successAction: c.SPLIT_BASIC_PRODUCTION_ADVANCEMENT_SUCCESS,
    errorAction: c.SPLIT_BASIC_PRODUCTION_ADVANCEMENT_ERROR,
    do: async () => {
      const productionService = ServiceContext.resolve<IProductionService>(
        AppServices.ProductionService
      );
      await productionService.splitBasicProductionAdvancement(id, params);
      radixToast({
        variant: 'success',
        title: I18n.t('production_advancement_split_successfully'),
        subtitle: I18n.t('you_can_check_it_on_the_table')
      });
    },
    errorHandler({ response }) {
      if (response?.data?.message === 'The Work step is duplicated.') {
        radixToast({
          variant: 'error',
          title: I18n.t('there_is_some_error'),
          subtitle: I18n.t('work_step_duplicated')
        });
        return;
      }
      if (
        response?.data?.message === 'The Production advancement has overlaps.'
      ) {
        radixToast({
          variant: 'error',
          title: I18n.t('there_is_some_error'),
          subtitle: I18n.t('production_advancement_has_overlaps')
        });
        return;
      }
      const error = response?.data?.message;
      radixToast({
        variant: 'error',
        title: I18n.t('there_is_some_error'),
        subtitle: error
      });
    }
  });

export const createFullProductionAdvancementBulk = (
  params: CreateFullProductionAdvancementBulkParams[]
) =>
  createServiceAction({
    doAction: c.CREATE_FULL_PRODUCTION_ADVANCEMENT_BULK,
    successAction: c.CREATE_FULL_PRODUCTION_ADVANCEMENT_BULK_SUCCESS,
    errorAction: c.CREATE_FULL_PRODUCTION_ADVANCEMENT_BULK_ERROR,
    do: async () => {
      const productionService = ServiceContext.resolve<IProductionService>(
        AppServices.ProductionService
      );
      const data = await productionService.createFullProductionAdvancementBulk(
        params
      );
      radixToast({
        variant: 'success',
        title: I18n.t('production_advancement_upload_complete'),
        subtitle: I18n.t('you_can_check_it_on_the_table')
      });
      return data;
    }
  });
export const createProductionDataBulk = (
  params: CreateProductionDataBulkParams[]
) =>
  createServiceAction({
    doAction: c.CREATE_PRODUCTION_DATA_BULK,
    successAction: c.CREATE_PRODUCTION_DATA_BULK_SUCCESS,
    errorAction: c.CREATE_PRODUCTION_DATA_BULK_ERROR,
    do: async () => {
      const productionService = ServiceContext.resolve<IProductionService>(
        AppServices.ProductionService
      );
      const data = await productionService.createProductionBulk(params);
      return data;
    }
  });

export const createBasicProductionAdvancementBulk = (
  params: CreateBasicProductionAdvancementParams[]
) =>
  createServiceAction({
    doAction: c.CREATE_BASIC_PRODUCTION_ADVANCEMENT_BULK,
    successAction: c.CREATE_BASIC_PRODUCTION_ADVANCEMENT_BULK_SUCCESS,
    errorAction: c.CREATE_BASIC_PRODUCTION_ADVANCEMENT_BULK_ERROR,
    do: async () => {
      const productionService = ServiceContext.resolve<IProductionService>(
        AppServices.ProductionService
      );
      const data = await productionService.createBasicProductionAdvancementBulk(
        params
      );
      radixToast({
        variant: 'success',
        title: I18n.t('production_advancement_upload_complete'),
        subtitle: I18n.t('you_can_check_it_on_the_table')
      });
      return data;
    }
  });

export const updateProductionData = (params: UpdateProductionDataParams) =>
  createServiceAction({
    doAction: c.UPDATE_PRODUCTION_DATA,
    successAction: c.UPDATE_PRODUCTION_DATA_SUCCESS,
    errorAction: c.UPDATE_PRODUCTION_DATA_ERROR,
    do: async () => {
      const productionService = ServiceContext.resolve<IProductionService>(
        AppServices.ProductionService
      );
      const data = await productionService.updateProduction(params);
      return data;
    },
    errorHandler({ response }) {
      if (response?.data?.message === 'The Work step has overlaps.') {
        toast.error(I18n.t('work_step_has_overlaps'));
        return;
      }
      if (!response || !response.data) {
        toastr.error(
          I18n.t('system_popup'),
          I18n.t('error_updating_production_batches')
        );
        return;
      }
      const { error } = response.data;
      toastr.error(I18n.t('system_popup'), error);
    }
  });

export const deleteProductionData = (params: DeleteProductionDataParams) =>
  createServiceAction({
    doAction: c.DELETE_PRODUCTION_DATA,
    successAction: c.DELETE_PRODUCTION_DATA_SUCCESS,
    errorAction: c.DELETE_PRODUCTION_DATA_ERROR,
    do: async () => {
      const productionService = ServiceContext.resolve<IProductionService>(
        AppServices.ProductionService
      );
      const data = await productionService.deleteProduction(params);
      return data;
    },
    errorHandler({ response }) {
      if (!response || !response.data) {
        toastr.error(
          I18n.t('system_popup'),
          I18n.t('error_deleting_production_batches')
        );
        return;
      }
      const { error } = response.data;
      toastr.error(I18n.t('system_popup'), error);
    }
  });

export const getCustomFields = (params: GetCustomFieldsParams) =>
  createServiceAction({
    doAction: c.FETCH_CUSTOM_FIELDS,
    successAction: c.FETCH_CUSTOM_FIELDS_SUCCESS,
    errorAction: c.FETCH_CUSTOM_FIELDS_ERROR,
    do: async (): Promise<{
      entity: ECustomFieldEntity;
      value: CustomFieldEntry[];
    }> => {
      const customFieldsService = ServiceContext.resolve<ICustomFieldsService>(
        AppServices.CustomFieldsService
      );
      const data = await customFieldsService.getCustomFields(params.entity);
      return data;
    },
    errorHandler({ response }) {
      if (!response || !response.data) {
        toastr.error(
          I18n.t('system_popup'),
          I18n.t('error_getting_custom_fields')
        );
        return;
      }
      const { error } = response.data;
      toastr.error(I18n.t('system_popup'), error);
    }
  });

export const fetchSettingsForProduction = () =>
  createServiceAction({
    doAction: c.FETCH_SETTINGS_FOR_PRODUCTION,
    successAction: c.FETCH_SETTINGS_FOR_PRODUCTION_SUCCESS,
    errorAction: c.FETCH_SETTINGS_FOR_PRODUCTION_ERROR,
    do: async (): Promise<{
      workOrderForProductionAdvancement: boolean;
    }> => {
      const productionService = ServiceContext.resolve<IProductionService>(
        AppServices.ProductionService
      );
      const data = await productionService.getSettingsForProduction();
      return data;
    },
    errorHandler({ response }) {
      if (!response || !response.data) {
        toastr.error(
          I18n.t('system_popup'),
          I18n.t('error_getting_production_settings')
        );
        return;
      }
      const { error } = response.data;
      toastr.error(I18n.t('system_popup'), error);
    }
  });

export const updateSettingsForProduction = (data: ProductionConfig) =>
  createServiceAction({
    doAction: c.UPDATE_SETTINGS_FOR_PRODUCTION,
    successAction: c.UPDATE_SETTINGS_FOR_PRODUCTION_SUCCESS,
    errorAction: c.UPDATE_SETTINGS_FOR_PRODUCTION_ERROR,
    do: async () => {
      const productionService = ServiceContext.resolve<IProductionService>(
        AppServices.ProductionService
      );
      await productionService.updateSettingsForProduction(data);
      radixToast({
        variant: 'success',
        title: I18n.t('changes_saved'),
        subtitle: I18n.t('changes_successfully_saved')
      });
      return data;
    },
    errorHandler({ response }) {
      if (!response || !response.data) {
        toastr.error(
          I18n.t('system_popup'),
          I18n.t('error_updating_production_settings')
        );
        return;
      }
      const { error } = response.data;
      toastr.error(I18n.t('system_popup'), error);
    }
  });

export const resetCreateBasicProductionAdvancementBulkSuccess = () => {
  return {
    type: c.RESET_CREATE_BASIC_PRODUCTION_ADVANCEMENT_BULK_SUCCESS
  };
};
